import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Row } from '~/commonComponents';
import { ShiftRequestRedesign } from '~/constants/ff.posthog';
import { DashboardJobsStatus, useGetDashboardJobs } from '~/domain/DashboardJobs';
import { getShouldEnforcePermanentJobV2 } from '~/growthbook';
import { useAppSelector, useMediaQuery } from '~/hooks';
import { useFeatureFlagVariant } from '~/hooks/useFeatureFlagVariant';
import { useUserSelector } from '~/hooks/useUserSelector';
import { routes } from '~/routes';
import { Colors } from '~/themes/colors';
import { fireDashboardActionNeededClicked } from '~/posthogs/events';
import { SelectProfessionTypePopup } from '../ShiftRequestRedesign/SelectProfessionType/SelectProfessionTypePopup';
import { useSelectProfessionType } from '../ShiftRequestRedesign/SelectProfessionType/useSelectProfessionType';
import { ShiftCard } from './ShiftCard';

interface IShiftsSectionProps {
  isLoading?: boolean;
}

export const ShiftsSection = ({ isLoading }: IShiftsSectionProps) => {
  const navigate = useNavigate();
  const dashboardInfo = useAppSelector((state) => state.job.dashboardInfo);
  const [isSmallScreen] = useMediaQuery(['lg']);
  const user = useUserSelector();
  const officeState = user?.address?.[0]?.state ?? '';

  const shouldUseShiftRequestRedesign = useFeatureFlagVariant(ShiftRequestRedesign, 'test');
  const { availableProfessions } = useSelectProfessionType({ officeState });
  const [isShowPopup, setIsShowPopup] = useState(false);

  const isPermV2Enabled = getShouldEnforcePermanentJobV2();

  const activeCount = dashboardInfo?.activeRequest;
  const actionRequiredCount = dashboardInfo?.actionRequired;
  const scheduledCount = dashboardInfo?.scheduled;

  const { data, isLoading: isLoadingActionRequired } = useGetDashboardJobs({
    status: DashboardJobsStatus.ACTION_REQUIRED,
    page: 1,
    limit: 15,
  });

  const openShiftsCount = parseInt(String(activeCount || 0), 10);
  const actionNeededCount = parseInt(String(actionRequiredCount || 0), 10);
  const scheduledShiftsCount = parseInt(String(scheduledCount || 0), 10);

  const handleOpenShiftsClick = () => {
    if (openShiftsCount === 0) {
      if (shouldUseShiftRequestRedesign) {
        setIsShowPopup(true);
      } else {
        navigate(routes.multiDayShiftRequest);
      }
    } else {
      navigate(routes.shiftsOverview.replace(':type', 'open'));
    }
  };

  const handleProfessionContinue = (professionCode: string) => {
    setIsShowPopup(false);
    navigate(routes.selectDates, { state: { professionType: professionCode } });
  };

  const handlePopupClose = () => {
    setIsShowPopup(false);
  };

  const handleActionNeededClick = () => {
    if (actionNeededCount === 0) {
      return;
    }

    fireDashboardActionNeededClicked();

    if (actionNeededCount === 1) {
      const counters = data?.[0]?.multipleCounters ?? [];
      const favoriteCandidate = counters.find((counter) => !!counter.isFavorite);

      const candidateId = (favoriteCandidate ?? counters?.[0])?.candidate?.id;
      const shiftId = data?.[0]?.id;

      if (candidateId && shiftId) {
        navigate(`/dashboard/job/${shiftId}/confirm/${candidateId}`);
        return;
      }
    }
    navigate(routes.shiftsOverview.replace(':type', 'pending'));
  };

  const handleScheduledClick = () => {
    navigate(routes.shiftsOverview.replace(':type', 'scheduled'));
  };

  return (
    <>
      <Row gap={20} width="100%">
        <ShiftCard
          title={isPermV2Enabled ? 'Open' : 'Open Shifts'}
          count={openShiftsCount}
          barColor={Colors.success_500}
          ctaText={
            isSmallScreen
              ? 'Click'
              : openShiftsCount === 0
                ? `Click to post${!isPermV2Enabled ? ' shifts' : ''}`
                : 'Click to view and invite'
          }
          isClickable
          onClick={handleOpenShiftsClick}
          isLoading={isLoading || isLoadingActionRequired}
          showCtaWhenEmpty
          dataCy="open-shifts-button"
          countDataCy="open-shifts-count"
        />

        <ShiftCard
          title={isSmallScreen || isPermV2Enabled ? 'Action Needed' : 'Action Needed Shifts'}
          count={actionNeededCount}
          barColor={Colors.error_450}
          countColor={Colors.error_450}
          ctaText={isSmallScreen ? 'Click' : 'Click to review candidates'}
          isClickable={actionNeededCount > 0}
          onClick={handleActionNeededClick}
          isLoading={isLoading || isLoadingActionRequired}
          emptyStateMessage="Once professionals apply to a posting, you'll be able to review them here."
          dataCy="action-needed-button"
          countDataCy="action-needed-count"
        />

        <ShiftCard
          title={isPermV2Enabled ? 'Scheduled' : 'Scheduled Shifts'}
          count={scheduledShiftsCount}
          barColor={Colors.secondary_500}
          ctaText={isSmallScreen ? 'Click' : 'Click to view'}
          isClickable={scheduledShiftsCount > 0}
          onClick={handleScheduledClick}
          isLoading={isLoading || isLoadingActionRequired}
          emptyStateMessage="Once you confirm a candidate for a posting, you'll be able to view them here."
          dataCy="scheduled-shifts-button"
          countDataCy="scheduled-shifts-count"
        />
      </Row>

      {isShowPopup && (
        <SelectProfessionTypePopup
          professions={availableProfessions}
          onContinue={handleProfessionContinue}
          onClose={handlePopupClose}
        />
      )}
    </>
  );
};
